import axios, { type AxiosInstance, type AxiosRequestConfig, type AxiosResponse, type AxiosError } from 'axios';
import type { ApiError, RateLimitHeaders } from '../types/api';
import { config } from '@lib/config';
import { generateRequestId } from '@utils/crypto';
import { isRetryableError } from '@lib/errors';
import { useSessionStore } from '../store/sessionStore';

export class ApiClient {
  private client: AxiosInstance;
  private refreshTokenPromise: Promise<string> | null = null;

  constructor() {
    this.client = axios.create({
      baseURL: config.api.baseUrl,
      timeout: config.api.timeout,
      headers: {
        'Content-Type': 'application/json',
      },
      // Enable credentials to send HttpOnly cookies with requests
      withCredentials: true,
    });

    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    // Request interceptor: Add auth token, request-id, idempotency-key
    this.client.interceptors.request.use(
      (config) => {
        // Add Request-Id for tracing
        const requestId = generateRequestId();
        config.headers['X-Request-Id'] = requestId;

        // Add auth token if available
        const token = this.getAccessToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        // Idempotency-Key should be added by caller for POST/PATCH
        // We just log it here for debugging
        if (config.headers['Idempotency-Key']) {
          if (import.meta.env.DEV) {
            console.log('[API] Idempotency-Key:', config.headers['Idempotency-Key']);
          }
        }

        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor: Handle rate limiting, token refresh, errors
    this.client.interceptors.response.use(
      (response) => {
        // Parse rate limit headers
        const rateLimitHeaders = this.parseRateLimitHeaders(response);
        if (rateLimitHeaders.remaining !== undefined && rateLimitHeaders.remaining < 10) {
          console.warn('[API] Rate limit warning:', rateLimitHeaders);
        }
        return response;
      },
      async (error: AxiosError<ApiError>) => {
        const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean; _retryCount?: number };

        // Auth endpoints must NOT trigger token refresh - a 401 here means
        // bad credentials / expired temp token / missing refresh cookie, NOT
        // an expired access token. Refreshing would swallow the real error
        // and surface it to the UI as a generic failure.
        const requestUrl = originalRequest.url || '';
        const isAuthEndpoint =
          requestUrl.includes('/api/v1/auth/login') ||
          requestUrl.includes('/api/v1/auth/register') ||
          requestUrl.includes('/api/v1/auth/refresh') ||
          requestUrl.includes('/api/v1/auth/2fa/verify') ||
          requestUrl.includes('/api/v1/auth/google') ||
          requestUrl.includes('/api/v1/auth/request-password-reset') ||
          requestUrl.includes('/api/v1/auth/reset-password') ||
          requestUrl.includes('/api/v1/auth/verify-email');

        // Handle 401 Unauthorized - Token expired (skip for auth endpoints)
        if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
          originalRequest._retry = true;

          try {
            const newToken = await this.refreshAccessToken();
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
            }
            return this.client(originalRequest);
          } catch (_refreshError) {
            // Refresh failed, logout user and reject with the normalized
            // ORIGINAL error (not the refresh error) so the caller sees a
            // consistent ApiError shape.
            this.handleAuthFailure();
            return Promise.reject(this.normalizeError(error));
          }
        }

        // Handle 403 TWO_FACTOR_SETUP_REQUIRED - Admin needs to enable 2FA
        // Redirect to security settings to set up 2FA - mandatory for admin roles
        if (error.response?.status === 403 && error.response.data?.code === 'TWO_FACTOR_SETUP_REQUIRED') {
          const currentPath = window.location.pathname;
          if (!currentPath.startsWith('/settings/security')) {
            window.location.href = '/settings/security?setup2fa=required';
          }
          return Promise.reject(this.normalizeError(error));
        }

        // Handle 429 Rate Limiting
        if (error.response?.status === 429 && config.rateLimit.retryEnabled) {
          const retryAfter = this.getRetryAfter(error.response);
          const retryCount = originalRequest._retryCount || 0;

          if (retryCount < config.rateLimit.maxRetries) {
            originalRequest._retryCount = retryCount + 1;
            console.log(`[API] Rate limited, retrying after ${retryAfter}ms (attempt ${retryCount + 1})`);
            
            await this.delay(retryAfter);
            return this.client(originalRequest);
          }
        }

        // Handle server errors with retry (5xx)
        if (isRetryableError(error.response?.status || 0)) {
          const retryCount = originalRequest._retryCount || 0;
          const maxRetries = config.media.maxRetries;

          if (retryCount < maxRetries) {
            originalRequest._retryCount = retryCount + 1;
            const delayMs = this.calculateBackoff(retryCount);
            console.log(`[API] Retrying request after ${delayMs}ms (attempt ${retryCount + 1})`);
            
            await this.delay(delayMs);
            return this.client(originalRequest);
          }
        }

        return Promise.reject(this.normalizeError(error));
      }
    );
  }

  private parseRateLimitHeaders(response: AxiosResponse): RateLimitHeaders {
    return {
      limit: response.headers['ratelimit-limit'] ? Number(response.headers['ratelimit-limit']) : undefined,
      remaining: response.headers['ratelimit-remaining'] ? Number(response.headers['ratelimit-remaining']) : undefined,
      reset: response.headers['ratelimit-reset'] ? Number(response.headers['ratelimit-reset']) : undefined,
      retryAfter: response.headers['retry-after'] ? Number(response.headers['retry-after']) * 1000 : undefined,
    };
  }

  private getRetryAfter(response: AxiosResponse): number {
    const retryAfter = response.headers['retry-after'];
    if (retryAfter) {
      return Number(retryAfter) * 1000; // Convert seconds to milliseconds
    }
    return config.media.retryDelayMs;
  }

  private calculateBackoff(retryCount: number): number {
    // Exponential backoff: baseDelay * 2^retryCount
    return config.media.retryDelayMs * Math.pow(2, retryCount);
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  private normalizeError(error: AxiosError<ApiError>): ApiError {
    if (error.response?.data) {
      return error.response.data;
    }

    // Network error or timeout
    if (error.code === 'ECONNABORTED' || error.message === 'Network Error') {
      return {
        statusCode: 0,
        message: 'NETWORK_ERROR',
        error: 'Network Error',
      };
    }

    return {
      statusCode: 500,
      message: 'INTERNAL_SERVER_ERROR',
      error: 'Unknown Error',
    };
  }

  // Auth token management
  private getAccessToken(): string | null {
    return localStorage.getItem('accessToken');
  }

  private setAccessToken(token: string): void {
    localStorage.setItem('accessToken', token);
  }

  // Note: getRefreshToken is no longer needed - refresh token is sent via HttpOnly cookie

  private async refreshAccessToken(): Promise<string> {
    // Prevent multiple simultaneous refresh requests
    if (this.refreshTokenPromise) {
      return this.refreshTokenPromise;
    }

    this.refreshTokenPromise = (async () => {
      try {
        // Refresh token is sent via HttpOnly cookie automatically
        // We send an empty body but the backend reads the cookie
        const response = await axios.post(
          `${config.api.baseUrl}/api/v1/auth/refresh`,
          {},
          { withCredentials: true }
        );

        // Backend wraps response in { data: {...}, traceId: string }
        const responseData = response.data.data || response.data;
        const { accessToken } = responseData;
        this.setAccessToken(accessToken);
        // Note: Refresh token is set as HttpOnly cookie by the backend, not in response body

        return accessToken;
      } finally {
        this.refreshTokenPromise = null;
      }
    })();

    return this.refreshTokenPromise;
  }

  private isHandlingAuthFailure = false;

  private handleAuthFailure(): void {
    // Prevent multiple simultaneous auth failure handling
    if (this.isHandlingAuthFailure) {
      return;
    }
    this.isHandlingAuthFailure = true;

    // Check if we're on auth pages (no need to show modal there)
    const currentPath = window.location.pathname;
    const authPaths = ['/login', '/register', '/forgot-password', '/reset-password', '/auth/', '/claim/'];
    const isAuthPage = authPaths.some(path => currentPath.startsWith(path));

    // Clear local storage
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    // Also clear Zustand persisted state
    localStorage.removeItem('tremoria-auth');

    if (!isAuthPage) {
      // Use session store to show modal
      useSessionStore.getState().setSessionExpired(true);
    }

    // Reset flag after a short delay to allow new auth failures to be handled
    // (e.g., if user dismisses modal and tries to do something again)
    setTimeout(() => {
      this.isHandlingAuthFailure = false;
    }, 1000);
  }

  // Unwrap API response - backend wraps responses in { data: T, traceId?: string }
  private unwrapResponse<T>(responseData: unknown): T {
    if (responseData && typeof responseData === 'object' && 'data' in responseData) {
      return (responseData as { data: T }).data;
    }
    return responseData as T;
  }

  // Public HTTP methods
  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.get<T>(url, config);
    return this.unwrapResponse<T>(response.data);
  }

  async post<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.post<T>(url, data, config);
    return this.unwrapResponse<T>(response.data);
  }

  async put<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.put<T>(url, data, config);
    return this.unwrapResponse<T>(response.data);
  }

  async patch<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.patch<T>(url, data, config);
    return this.unwrapResponse<T>(response.data);
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.delete<T>(url, config);
    return this.unwrapResponse<T>(response.data);
  }

  // POST with FormData (for file uploads)
  async postFormData<T>(url: string, formData: FormData, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.post<T>(url, formData, {
      ...config,
      headers: {
        ...config?.headers,
        'Content-Type': 'multipart/form-data',
      },
    });
    return this.unwrapResponse<T>(response.data);
  }

  // Get raw axios instance for special cases (e.g. upload progress)
  getAxiosInstance(): AxiosInstance {
    return this.client;
  }
}

// Singleton instance
export const apiClient = new ApiClient();
